import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { FormField, TextInputType } from '../models/form.model';
import { FormValidationService } from '../service/form-validation.service';

@Component({
  selector: 'app-form-text-field',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  template: `
    <div class="form-group">
      <label [for]="field.id" class="form-label">
        {{ field.label }}
        <span *ngIf="field.required" class="required">*</span>
      </label>
      <div *ngIf="field.helpText" class="help-text">{{ field.helpText }}</div>
      
      <!-- Rich Text Editor Mode -->
      <div *ngIf="inputMode === 'richtext'" class="editor-wrapper" [class.readonly]="readonly">
        <quill-editor
          *ngIf="!readonly"
          class="rich-editor"
          [class.is-invalid]="hasError"
          [id]="field.id"
          [modules]="quillModules"
          [placeholder]="field.label"
          [readOnly]="readonly"
          [ngModel]="value"
          (ngModelChange)="onEditorModelChange($event)"
          (onContentChanged)="onEditorContentChanged($event)"
        ></quill-editor>

        <div class="readonly-wrapper" *ngIf="readonly">
          <quill-view-html
            class="rich-view"
            [content]="value || ''"
            theme="snow"
          ></quill-view-html>

          <button
            *ngIf="value"
            type="button"
            class="copy-button"
            [class.success]="copyFeedbackState === 'success'"
            [class.error]="copyFeedbackState === 'error'"
            [title]="copyFeedbackState === 'idle' ? 'Copy to clipboard' : copyFeedbackMessage"
            (click)="onCopyClick()"
            [disabled]="copyFeedbackState !== 'idle'"
            [attr.aria-label]="'Copy to clipboard'"
          >
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'idle'">content_copy</span>
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'success'">check_circle</span>
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'error'">error</span>
          </button>
        </div>
      </div>

      <!-- Plain Text (Multi-line) Mode -->
      <div *ngIf="inputMode === 'plaintext'" class="plain-wrapper" [class.readonly]="readonly">
        <textarea
          *ngIf="!readonly"
          class="form-control"
          [class.is-invalid]="hasError"
          [id]="field.id"
          [placeholder]="field.label"
          [ngModel]="value"
          (ngModelChange)="onPlainTextChange($event)"
          rows="5"
        ></textarea>

        <div class="readonly-plain" *ngIf="readonly">
          <pre class="plain-text-view">{{ value || '' }}</pre>
          <button
            *ngIf="value"
            type="button"
            class="copy-button"
            [class.success]="copyFeedbackState === 'success'"
            [class.error]="copyFeedbackState === 'error'"
            [title]="copyFeedbackState === 'idle' ? 'Copy to clipboard' : copyFeedbackMessage"
            (click)="onCopyClick()"
            [disabled]="copyFeedbackState !== 'idle'"
            [attr.aria-label]="'Copy to clipboard'"
          >
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'idle'">content_copy</span>
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'success'">check_circle</span>
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'error'">error</span>
          </button>
        </div>
      </div>

      <!-- Single Line Text Mode -->
      <div *ngIf="inputMode === 'singleline'" class="single-wrapper" [class.readonly]="readonly">
        <input
          *ngIf="!readonly"
          type="text"
          class="form-control"
          [class.is-invalid]="hasError"
          [id]="field.id"
          [placeholder]="field.label"
          [ngModel]="value"
          (ngModelChange)="onPlainTextChange($event)"
        />

        <div class="readonly-single" *ngIf="readonly">
          <span class="single-text-view">{{ value || '' }}</span>
          <button
            *ngIf="value"
            type="button"
            class="copy-button"
            [class.success]="copyFeedbackState === 'success'"
            [class.error]="copyFeedbackState === 'error'"
            [title]="copyFeedbackState === 'idle' ? 'Copy to clipboard' : copyFeedbackMessage"
            (click)="onCopyClick()"
            [disabled]="copyFeedbackState !== 'idle'"
            [attr.aria-label]="'Copy to clipboard'"
          >
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'idle'">content_copy</span>
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'success'">check_circle</span>
            <span class="material-icons copy-icon" *ngIf="copyFeedbackState === 'error'">error</span>
          </button>
        </div>
      </div>

      <div *ngIf="copyFeedbackState !== 'idle'" class="copy-feedback" [class.success]="copyFeedbackState === 'success'" [class.error]="copyFeedbackState === 'error'">
        {{ copyFeedbackMessage }}
      </div>

      <div *ngIf="hasError && error" class="invalid-feedback">
        {{ validationService.getErrorMessage(field, error) }}
      </div>
    </div>
  `,
  styles: [`
    .form-group { margin-bottom: 1rem; }
    .form-label { font-weight: 500; display: block; margin-bottom: 0.5rem; }
    .required { color: red; }
    .help-text { font-size: 0.875rem; color: #6c757d; margin-bottom: 0.25rem; }
    .editor-wrapper { position: relative; }
    .plain-wrapper { position: relative; }
    .single-wrapper { position: relative; }
    .readonly-wrapper { position: relative; padding-right: 48px; min-height: 120px; border: 1px solid #ced4da; border-radius: 0.25rem; padding: 0.75rem 48px 0.75rem 0.75rem; background-color: #f8f9fa; word-wrap: break-word; overflow-wrap: break-word; }
    .readonly-plain { position: relative; padding-right: 48px; min-height: 120px; border: 1px solid #ced4da; border-radius: 0.25rem; padding: 0.75rem 48px 0.75rem 0.75rem; background-color: #f8f9fa; }
    .readonly-single { position: relative; display: flex; align-items: center; padding-right: 48px; min-height: 38px; border: 1px solid #ced4da; border-radius: 0.25rem; padding: 0.375rem 48px 0.375rem 0.75rem; background-color: #f8f9fa; }
    .form-control { display: block; width: 100%; padding: 0.375rem 0.75rem; font-size: 1rem; line-height: 1.5; color: #495057; background-color: #fff; border: 1px solid #ced4da; border-radius: 0.25rem; transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out; }
    .form-control:focus { border-color: #80bdff; outline: 0; box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25); }
    .form-control.is-invalid { border-color: #dc3545; }
    .form-control.is-invalid:focus { border-color: #dc3545; box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25); }
    textarea.form-control { resize: vertical; }
    .plain-text-view { margin: 0; padding: 0; font-family: inherit; font-size: 1rem; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word; }
    .single-text-view { display: inline-block; word-break: break-word; }
    :host ::ng-deep .rich-editor { display: block; width: 100%; }
    :host ::ng-deep .rich-editor .ql-toolbar { border-radius: 0.25rem 0.25rem 0 0; border: 1px solid #ced4da; }
    :host ::ng-deep .rich-editor .ql-container { min-height: 120px; border-radius: 0 0 0.25rem 0.25rem; border: 1px solid #ced4da; border-top: none; }
    :host ::ng-deep .rich-editor.is-invalid .ql-toolbar { border-color: #dc3545; }
    :host ::ng-deep .rich-editor.is-invalid .ql-container { border-color: #dc3545; }
    :host ::ng-deep .rich-editor .ql-editor ol,
    :host ::ng-deep .rich-editor .ql-editor ul { margin: 0 0 1em 0; padding-left: 1.5em; }
    .rich-view { border: none; background: none; padding: 0; }
    :host ::ng-deep .rich-view .ql-container { padding: 0 !important; margin: 0 !important; border: none !important; background: none !important; }
    :host ::ng-deep .rich-view .ql-editor { padding: 0 !important; margin: 0 !important; border: none !important; background: none !important; }
    :host ::ng-deep .rich-view .ql-clipboard { display: none !important; }
    :host ::ng-deep .rich-view p { margin: 0 0 1em 0; }
    :host ::ng-deep .rich-view p:last-child { margin-bottom: 0; }
    :host ::ng-deep .rich-view ul, :host ::ng-deep .rich-view ol { margin: 0 0 1em 0; padding-left: 1.5em; }
    :host ::ng-deep .rich-view ul:last-child, :host ::ng-deep .rich-view ol:last-child { margin-bottom: 0; }
    :host ::ng-deep .rich-view ul { list-style-type: disc !important; }
    :host ::ng-deep .rich-view ol { list-style-type: decimal !important; }
    :host ::ng-deep .rich-view ul li, :host ::ng-deep .rich-view ol li { list-style-position: outside !important; list-style: revert !important; }
    :host ::ng-deep .rich-view .ql-editor li { list-style: revert !important; }
    :host ::ng-deep .rich-view .ql-editor ul li { list-style-type: disc !important; }
    :host ::ng-deep .rich-view .ql-editor ol li { list-style-type: decimal !important; }
    .copy-button {
      position: absolute;
      top: 8px;
      right: 8px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #6c757d;
      transition: color 0.2s ease;
    }
    .copy-button:hover:not(:disabled) { color: #495057; }
    .copy-button:disabled { cursor: not-allowed; opacity: 0.6; }
    .copy-button:focus { outline: 2px solid #0d6efd; border-radius: 2px; }
    .copy-button.success { color: #28a745; }
    .copy-button.error { color: #dc3545; }
    .copy-icon { font-size: 20px; width: 20px; height: 20px; }
    .copy-feedback { font-size: 0.875rem; margin-top: 0.25rem; padding: 4px 8px; border-radius: 4px; display: inline-block; }
    .copy-feedback.success { color: #155724; background-color: #d4edda; border: 1px solid #c3e6cb; }
    .copy-feedback.error { color: #721c24; background-color: #f8d7da; border: 1px solid #f5c6cb; }
    .invalid-feedback { color: #dc3545; font-size: 0.875rem; margin-top: 0.25rem; }
  `]
})
export class FormTextFieldComponent implements OnInit {
  @Input() field!: FormField;
  @Input() value: string | undefined;
  @Input() readonly: boolean = false; // Flag to indicate if field is in read-only/view mode
  @Output() valueChange = new EventEmitter<string>();
  @Output() errorChange = new EventEmitter<any>();
  @Output() copySuccess = new EventEmitter<void>(); // Emitted when copy succeeds
  @Output() copyError = new EventEmitter<Error>(); // Emitted when copy fails

  error: any = null;
  hasError = false;
  copyFeedbackState: 'idle' | 'success' | 'error' = 'idle'; // Track visual feedback state
  copyFeedbackMessage = ''; // Message to display in feedback

  // Getter to determine input mode, default to 'richtext' for backward compatibility
  get inputMode(): TextInputType {
    return this.field.inputType || 'richtext';
  }

  // Toolbar defaults for a compact rich-text experience
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  constructor(public validationService: FormValidationService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.validateField();
  }

  onEditorModelChange(content: string): void {
    const normalized = this.normalizeValue(content);
    this.value = normalized;
    this.valueChange.emit(this.value);
    this.validateField();
  }

  onEditorContentChanged(event: { html?: string | null }): void {
    if (event && (event.html === null || event.html === '<p><br></p>')) {
      this.onEditorModelChange('');
    }
  }

  onPlainTextChange(content: string): void {
    this.value = content || '';
    this.valueChange.emit(this.value);
    this.validateField();
  }

  onCopyClick(): void {
    const content = this.value || '';
    if (!content) {
      return;
    }

    // For plaintext and singleline modes, copy as plain text only
    if (this.inputMode === 'plaintext' || this.inputMode === 'singleline') {
      this.copyPlainText(content);
      return;
    }

    // For richtext mode, copy with formatting
    const htmlContent = content;
    const plainContent = this.getPlainText(content);
    
    // Prefer rich copy with both HTML and plain text when supported
    if (navigator.clipboard && window.isSecureContext && typeof ClipboardItem !== 'undefined') {
      const item = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainContent], { type: 'text/plain' })
      });

      navigator.clipboard.write([item])
        .then(() => {
          console.log('[FormTextField] Rich text copied to clipboard');
          this.showSuccessFeedback();
          this.copySuccess.emit();
        })
        .catch((err) => {
          console.error('[FormTextField] Failed to copy rich text:', err);
          this.copyError.emit(err);
          // Fallback to plain text copy
          this.copyPlainText(plainContent);
        });
      return;
    }

    // Fallback to plain text when rich copy not available
    this.copyPlainText(plainContent);
  }

  private copyPlainText(content: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(content)
        .then(() => {
          console.log('[FormTextField] Text copied to clipboard');
          this.showSuccessFeedback();
          this.copySuccess.emit();
        })
        .catch((err) => {
          console.error('[FormTextField] Failed to copy text:', err);
          this.copyError.emit(err);
          this.copyUsingExecCommand(content, false);
        });
      return;
    }

    this.copyUsingExecCommand(content, true);
  }

  private copyUsingExecCommand(content: string, rich: boolean): void {
    // Use a hidden element to preserve formatting when possible
    const element = document.createElement('div');
    element.innerHTML = rich ? content : this.escapeHtml(content);
    element.contentEditable = 'true';
    element.style.position = 'fixed';
    element.style.opacity = '0';
    element.style.pointerEvents = 'none';
    document.body.appendChild(element);

    const range = document.createRange();
    range.selectNodeContents(element);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    try {
      const success = document.execCommand('copy');
      if (success) {
        console.log('[FormTextField] Text copied to clipboard (fallback)');
        this.showSuccessFeedback();
        this.copySuccess.emit();
      } else {
        throw new Error('execCommand copy returned false');
      }
    } catch (err) {
      console.error('[FormTextField] Fallback copy failed:', err);
      this.showErrorFeedback('Failed to copy');
      this.copyError.emit(err as Error);
    } finally {
      selection?.removeAllRanges();
      document.body.removeChild(element);
    }
  }

  private showSuccessFeedback(): void {
    this.copyFeedbackState = 'success';
    this.copyFeedbackMessage = 'Copied to clipboard!';
    this.cdr.markForCheck(); // Trigger change detection
    // Reset to idle after 2 seconds
    setTimeout(() => {
      this.copyFeedbackState = 'idle';
      this.copyFeedbackMessage = '';
      this.cdr.markForCheck(); // Trigger change detection on reset
    }, 2000);
  }

  private showErrorFeedback(message: string): void {
    this.copyFeedbackState = 'error';
    this.copyFeedbackMessage = message;
    this.cdr.markForCheck(); // Trigger change detection
    // Reset to idle after 2 seconds
    setTimeout(() => {
      this.copyFeedbackState = 'idle';
      this.copyFeedbackMessage = '';
      this.cdr.markForCheck(); // Trigger change detection on reset
    }, 2000);
  }

  private validateField(): void {
    this.error = this.validationService.validateField(this.field, this.value);
    this.hasError = !!this.error;
    this.errorChange.emit(this.error);
  }

  private normalizeValue(content: string | null | undefined): string {
    if (!content || content === '<p><br></p>') {
      return '';
    }
    return content;
  }

  private getPlainText(content: string | null | undefined): string {
    if (!content) {
      return '';
    }
    const container = document.createElement('div');
    container.innerHTML = content;
    return container.textContent || '';
  }

  private escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}
